import React, { useState, useCallback, useEffect } from "react";
import {
  Drawer,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Box,
} from "@material-ui/core";
import clsx from "clsx";
import { useStyles } from "./NotesDrawer.styles";
import useNotesContext from "../../../hooks/useNotesContext";
import useNotesPermissions from "../../../hooks/useNotesPermissions";
import NotesList from "../NotesList/NotesList";
import AddNoteForm from "../AddNoteForm/AddNoteForm";

const NotesDrawer = ({ open, onClose }) => {
  const classes = useStyles();
  const { notes, isLoading, error } = useNotesContext();
  const permissions = useNotesPermissions();
  const [isAdding, setIsAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const [buttonsFading, setButtonsFading] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsAdding(false);
      setShowForm(false);
      setAnimating(false);
      setShowButtons(true);
      setButtonsFading(false);
    }
  }, [open]);

  const handleOpenForm = () => {
    setButtonsFading(true);
  };

  const handleButtonsFadeEnd = () => {
    if (buttonsFading) {
      setShowButtons(false);
      setButtonsFading(false);
      setShowForm(true);
      setIsAdding(true);
    }
  };

  const handleCloseForm = () => {
    setAnimating(true);
  };

  const handleAnimationEnd = () => {
    if (animating) {
      setShowForm(false);
      setIsAdding(false);
      setAnimating(false);
      setShowButtons(true);
    }
  };

  const handleRetry = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Drawer
      className={clsx("drawer")}
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
      BackdropProps={{}}
    >
      <div className={classes.container}>
        <div className={clsx(classes.header, { [classes.contentBlurred]: isAdding })}>
          <Typography variant="h5" color="primary" component="h2" className="notes-title">
            Notes
          </Typography>
        </div>
        <Divider />

        <div className={clsx(classes.content, { [classes.contentBlurred]: isAdding })}>
          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress className="loading-spinner" />
            </Box>
          )}

          {error && !isLoading && (
            <div className={classes.errorContainer}>
              <Typography color="error" gutterBottom className="error-message">
                {error}
              </Typography>
              <Button variant="outlined" onClick={handleRetry} className="retry-button">
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !error && notes.length === 0 && (
            <Typography color="textPrimary" variant="body1" className="no-notes-message">
              No notes configured
            </Typography>
          )}

          {!isLoading && !error && notes.length > 0 && (
            <NotesList notes={notes} />
          )}
        </div>

        {showForm && (
          <div
            className={clsx(
              classes.addNoteFormSection,
              "animate__animated",
              animating ? "animate__fadeOutDown" : "animate__fadeInUp"
            )}
            onAnimationEnd={handleAnimationEnd}
          >
            <AddNoteForm onCancel={handleCloseForm} />
          </div>
        )}

        <Divider />
        <div className={classes.footer}>
          {showButtons && (
            <div
              className={clsx(
                classes.footerButtons,
                "animate__animated",
                buttonsFading ? "animate__fadeOut" : "animate__fadeIn"
              )}
              onAnimationEnd={handleButtonsFadeEnd}
            >
              <Button
                variant="contained"
                onClick={onClose}
                className="close-button"
              >
                Close
              </Button>
              {permissions.canEdit && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenForm}
                  className="add-note-button"
                >
                  Add Note
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default NotesDrawer;
